import Trinity from '@core/scripts/components/trinity-adapter.js';

/**
 * Trinity tracking events for the header search field.
 * Event names and versions are defined by the Tracking Team spec
 * (CLICK_HEADER_ICON v2, LAYERVIEW_SEARCH_LAYER v1, CLICK_SEARCH_LAYER v1).
 *
 * The LAYERVIEW_SEARCH_LAYER / CLICK_SEARCH_LAYER / CLICK_SEARCH_LAYER_INPUT events
 * are only wired into the new (Algolia) search overlay — never the legacy Fredhopper search.
 */
export const SearchTrinityEvents = {
    /** Fired on every click of the header search (magnifier) icon. */
    sendSearchIconClick: () => {
        Trinity.sendTrinityEvent({
            schema: { version: 2, name: 'CLICK_HEADER_ICON' },
            attributes: { clickName: 'search', object: 'icon' },
        });
    },

    /** Fired once each time the search layer/overlay is displayed to the user. */
    sendSearchLayerView: () => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'LAYERVIEW_SEARCH_LAYER' },
            attributes: { layerViewName: 'searchLayer' },
        });
    },

    /** Fired when the user closes the search layer via the 'X' icon. */
    sendSearchLayerCloseX: () => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER' },
            attributes: { clickName: 'searchLayerCloseX', object: 'xIcon' },
        });
    },

    /** Fired when the user closes the search layer by clicking the background/scrim. */
    sendSearchLayerCloseBackground: () => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER' },
            attributes: { clickName: 'searchLayerCloseBackground', object: 'backdrop' },
        });
    },

    /** Fired when the user clicks the generic "view all results" link inside the layer. */
    sendSearchLayerAllResults: () => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER' },
            attributes: { clickName: 'allSearchResults', object: 'link' },
        });
    },

    /** Fired when the user clicks directly into the search input field to enter a term. */
    sendSearchInputEnterTerm: (searchTerm) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER_INPUT' },
            attributes: { clickName: 'enterSearchTerm', searchTerm },
        });
    },

    /** Fired when the user submits the typed term via the search button or the Enter key. */
    sendSearchInputSubmit: (searchTerm) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER_INPUT' },
            attributes: { clickName: 'submitSearch', searchTerm, object: 'button' },
        });
    },

    /** Fired when the user clears the field via the "delete"/"clear" link (term before deletion). */
    sendSearchInputDeleteTerm: (searchTerm) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_LAYER_INPUT' },
            attributes: { clickName: 'deleteSearchTerm', searchTerm, object: 'link' },
        });
    },

    /**
     * Fired each time the term-suggestion list becomes visible or changes to a new set of terms
     * while the user types. `suggestedTerm` is the list of currently-visible suggested terms;
     * `currentSearchInput` is the text those suggestions were fetched for.
     */
    sendSearchSuggestionTermView: (suggestedTerm, currentSearchInput) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'VIEW_SEARCH_SUGGESTION_TERM' },
            attributes: { viewName: 'searchSuggestionTerm', suggestedTerm, currentSearchInput },
        });
    },

    /**
     * Fired when the user chooses a suggested term from the suggestion list (pointer click or
     * keyboard selection). `amountOfSearchResult` is the number of product suggestions shown in
     * the overlay for that term.
     */
    sendSearchSuggestionTermClick: (suggestedTerm, currentSearchInput, amountOfSearchResult) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_SUGGESTION_TERM' },
            attributes: {
                clickName: 'clickSuggestedTerm',
                suggestedTerm,
                currentSearchInput,
                amountOfSearchResult,
                object: 'link',
            },
        });
    },

    /**
     * Fired each time the product-suggestion list becomes visible or changes to a new set while the
     * user types. `itemId` is the list of itemIds of the currently-visible suggested products;
     * `currentSearchInput` is the text those suggestions were fetched for.
     */
    sendSearchSuggestionProductView: (itemId, currentSearchInput) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'VIEW_SEARCH_SUGGESTION_PRODUCT' },
            attributes: { viewName: 'searchSuggestionProduct', itemId, currentSearchInput },
        });
    },

    /**
     * Fired when the user clicks a suggested product in the suggestion list. `itemId` is the
     * clicked product's itemId (numeric); `currentSearchInput` is the text the user had typed.
     */
    sendSearchSuggestionProductClick: (itemId, currentSearchInput) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_SUGGESTION_PRODUCT' },
            attributes: { clickName: 'clickSuggestedProduct', itemId, currentSearchInput, object: 'link' },
        });
    },

    /** Fired once per overlay-open when the "Recent Searches" section becomes visible. */
    sendSearchRecentSearchesView: (visibleSearchTerms) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'VIEW_SEARCH_RECENT_SEARCHES' },
            attributes: { viewName: 'recentSearchesSection', visibleSearchTerms },
        });
    },

    /** Fired once per overlay-open when the "Popular Searches" section becomes visible. */
    sendSearchPopularSearchesView: (visibleSearchTerms) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'VIEW_SEARCH_POPULAR_SEARCHES' },
            attributes: { viewName: 'popularSearchesSection', visibleSearchTerms },
        });
    },

    /**
     * Fired when the user interacts with the "Recent Searches" section. `clickName` is one of
     * showMoreRecentSearches | showLessRecentSearches | deleteAllRecentSearches |
     * clickRecentSearchTerm | copyRecentSearchTerm. `searchTerm` and `position` are mandatory for
     * every action (Trinity schema), so the non-term actions send the '' / -1 "not applicable"
     * sentinels; the term actions (clickRecentSearchTerm, copyRecentSearchTerm) pass real values.
     */
    sendSearchRecentSearchesAction: (clickName, searchTerm = '', position = -1) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_RECENT_SEARCHES_ACTION' },
            attributes: { clickName, searchTerm, position, object: 'link' },
        });
    },

    /**
     * Fired when the user interacts with the "Popular Searches" section. `clickName` is one of
     * clickPopularSearchTerm | showMorePopularSearches | showLessPopularSearches. `searchTerm` and
     * `position` are mandatory for every action (Trinity schema), so the toggle actions send the
     * '' / -1 "not applicable" sentinels; clickPopularSearchTerm passes real values.
     */
    sendSearchPopularSearchesAction: (clickName, searchTerm = '', position = -1) => {
        Trinity.sendTrinityEvent({
            schema: { version: 1, name: 'CLICK_SEARCH_POPULAR_SEARCHES_ACTION' },
            attributes: { clickName, searchTerm, position, object: 'link' },
        });
    },
};
